import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextInputOnly from './TextInputOnly'
import InputChip, { INPUT_CHIP_HEIGHT } from 'LuxKit/Chips/InputChip'
import noop from 'lib/function/noop'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useResizeObserver from 'hooks/useResizeObserver'
import { getElementTextDirection } from 'lib/dom/logicalScroll'

export interface ChipMultiSelectInputOption {
  value: string
  label: string
}

interface Props extends Omit<React.ComponentProps<typeof TextInputOnly>, 'onChange' | 'startContent' | 'value'> {
  selectedOptions: Array<ChipMultiSelectInputOption>
  value?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onRemoveOption: (value: string) => void
  expanded?: boolean
  expandedMaxWidth?: number
}

const EXPANDED_VIEWPORT_END_MARGIN = 48
const DEFAULT_EXPANDED_MAX_WIDTH = 500
const CHIP_INPUT_MIN_WIDTH = rem(160)
const CHIP_ROW_HEIGHT = rem(INPUT_CHIP_HEIGHT)

const StyledInputChip = styled(InputChip)`
  flex: 0 0 auto;
`

const StartIconWrapper = styled.span`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: ${CHIP_ROW_HEIGHT};
  padding-inline-start: ${rem(12)};
`

const FieldWrapper = styled.div<{ $expandedHeight?: number }>`
  position: relative;
  min-height: ${props => props.$expandedHeight ? `${props.$expandedHeight}px` : rem(50)};
`

const ChipList = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  gap: ${rem(4)};
  min-width: 0;
  width: auto;
  max-width: calc(100% - ${CHIP_INPUT_MIN_WIDTH});
  overflow-x: auto;
  scrollbar-width: none;
  padding-inline-start: ${rem(12)};

  &::-webkit-scrollbar {
    display: none;
  }
`

const Field = styled(TextInputOnly)<{ $expandedWidth?: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: ${rem(50)};
  overflow: hidden;

  > input {
    flex: 1 0 ${rem(48)};
    min-width: ${rem(48)};
    width: auto;
  }

  &.has-chips > input {
    flex: 1 0 ${CHIP_INPUT_MIN_WIDTH};
    min-width: ${CHIP_INPUT_MIN_WIDTH};
    padding-inline-start: ${rem(4)};
  }

  &.has-chips:not(.expanded) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: ${rem(8)} ${rem(4)};
    padding: ${rem(10)} ${rem(12)};

    ${StartIconWrapper} {
      padding-inline-start: 0;
    }

    ${ChipList} {
      display: contents;
    }

    > input {
      height: ${CHIP_ROW_HEIGHT};
      line-height: ${CHIP_ROW_HEIGHT};
      padding: 0;
    }
  }

  &.expanded {
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: 0;
    z-index: 1;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: ${rem(8)} ${rem(4)};
    padding: ${rem(10)} ${rem(12)};
    width: ${props => props.$expandedWidth ? `${props.$expandedWidth}px` : '100%'};
    min-width: 100%;
    max-width: ${props => props.$expandedWidth ? `${props.$expandedWidth}px` : '100%'};
    overflow: visible;

    ${StartIconWrapper} {
      padding-inline-start: 0;
    }

    ${ChipList} {
      display: contents;
    }

    > input {
      flex: 1 1 ${rem(240)};
      min-width: ${CHIP_INPUT_MIN_WIDTH};
      height: ${CHIP_ROW_HEIGHT};
      line-height: ${CHIP_ROW_HEIGHT};
      padding: 0;
    }

    &.has-chips > input {
      flex-basis: ${CHIP_INPUT_MIN_WIDTH};
    }
  }
`

const ChipMultiSelectInput = forwardRef<HTMLInputElement | null, Props>((props, ref) => {
  const {
    selectedOptions,
    onRemoveOption,
    placeholder,
    value = '',
    onChange,
    className,
    onClick,
    expanded,
    expandedMaxWidth = DEFAULT_EXPANDED_MAX_WIDTH,
    startIcon,
    ...inputProps
  } = props
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const fieldContainerRef = useRef<HTMLDivElement>(null)
  const [expandedWidth, setExpandedWidth] = useState<number>()
  const [expandedHeight, setExpandedHeight] = useState<number>()

  const updateExpandedWidth = useCallback(() => {
    const wrapperElement = wrapperRef.current

    if (!wrapperElement || !expanded) {
      setExpandedWidth(undefined)
      return
    }

    const wrapperRect = wrapperElement.getBoundingClientRect()
    // The expanded field grows from the inline-start edge, so measure the space towards the inline-end side of the viewport
    const inlineEndSpace = getElementTextDirection(wrapperElement) === 'rtl' ? wrapperRect.right : window.innerWidth - wrapperRect.left
    const availableViewportWidth = inlineEndSpace - EXPANDED_VIEWPORT_END_MARGIN
    const nextExpandedWidth = Math.max(
      wrapperRect.width,
      Math.min(availableViewportWidth, expandedMaxWidth),
    )

    setExpandedWidth(nextExpandedWidth)
  }, [expanded, expandedMaxWidth])

  const updateExpandedHeight = useCallback(() => {
    const fieldContainerElement = fieldContainerRef.current

    if (!fieldContainerElement || !expanded || !selectedOptions.length) {
      setExpandedHeight(undefined)
      return
    }

    setExpandedHeight(fieldContainerElement.getBoundingClientRect().height)
  }, [expanded, selectedOptions.length])

  useIsomorphicLayoutEffect(() => {
    updateExpandedWidth()
    updateExpandedHeight()
  }, [updateExpandedHeight, updateExpandedWidth, selectedOptions.length])

  useEffect(() => {
    if (!expanded) {
      return
    }

    window.addEventListener('resize', updateExpandedWidth)

    return () => window.removeEventListener('resize', updateExpandedWidth)
  }, [expanded, updateExpandedWidth])

  useResizeObserver(wrapperRef, updateExpandedWidth)
  useResizeObserver(fieldContainerRef, updateExpandedHeight)

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus()
    }
  }, [expanded])

  const handleInputRef = useCallback((element: HTMLInputElement | null) => {
    inputRef.current = element

    if (typeof ref === 'function') {
      ref(element)
      return
    }

    if (ref) {
      ref.current = element
    }
  }, [ref])

  const handleRemoveOption = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onRemoveOption(event.currentTarget.value)
  }, [onRemoveOption])

  // Mouse removal happens on mouse down so focus stays in the text input; keyboard activation still arrives as a click.
  const handleClickOption = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (event.detail === 0) {
      onRemoveOption(event.currentTarget.value)
    }
  }, [onRemoveOption])

  const handleFieldWrapperMouseDown = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const chips = useMemo(() => {
    if (!selectedOptions.length) {
      return undefined
    }

    return <ChipList data-testid="chip-multi-select-input-chips">
      {selectedOptions.map((option) => (
        <StyledInputChip
          key={option.value}
          type="button"
          value={option.value}
          variant="filled"
          aria-label={intl.formatMessage(
            {
              id: 'Yu0UkstO8_gdE-WFkaib',
              defaultMessage: 'Remove {label}',
              description: 'Accessible label for removing a selected value chip from a multi-select input',
            },
            { label: option.label },
          )}
          onMouseDown={handleRemoveOption}
          onClick={handleClickOption}
        >
          {option.label}
        </StyledInputChip>
      ))}
    </ChipList>
  }, [handleClickOption, handleRemoveOption, intl, selectedOptions])

  const startContent = useMemo(() => {
    if (!startIcon && !chips) {
      return undefined
    }

    return <>
      {startIcon && <StartIconWrapper>{startIcon}</StartIconWrapper>}
      {chips}
    </>
  }, [chips, startIcon])

  const fieldClassName = clsx(
    className,
    {
      'has-chips': selectedOptions.length > 0,
      'expanded': expanded,
    },
  )

  return <FieldWrapper ref={wrapperRef} $expandedHeight={expandedHeight} onMouseDown={handleFieldWrapperMouseDown}>
    <Field
      {...inputProps}
      className={fieldClassName}
      containerRef={fieldContainerRef}
      ref={handleInputRef}
      $expandedWidth={expandedWidth}
      value={value}
      placeholder={placeholder}
      startContent={startContent}
      onChange={onChange}
      onClick={onClick ?? noop}
    />
  </FieldWrapper>
})

ChipMultiSelectInput.displayName = 'ChipMultiSelectInput'

export default ChipMultiSelectInput
